import { Component, OnInit, ViewChild } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { MaiaUploadStepComponent } from "../maia-upload-step/maia-upload-step.component";
import { MaiaFlowStepId } from "../maia-step-header/maia-step-header.component";
import { MaiaReviewSubmit } from "../maia-review-step/maia-review-step.component";
import { MaiaColumnMappingService } from "../../data-access-layer/maia/maia-column-mapping.service";
import { NotifService } from "../../core/notification/notif.service";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";
import { MaiaGroupView } from "../maia-group-step/maia-group-step.component";
import { formatFileSize } from "../maia-file-size.util";
import { SovFieldMapping } from "../../model/maia/sov-field-mapping";
import { SovUploadResponse, SovUploadStatus } from "../../model/maia/sov-upload";
import { MaiaScreen, MaiaWizardStep, SovActiveJobSummary, SovJobState } from "../../model/maia/maia-wizard-progress";

type MaiaRightPanel =
    | "upload"
    | "review"
    | "geocode"
    | "enrich"
    | "enhance"
    | "ready"
    | "report"
    | "prepare"
    | "group"
    | null;type MaiaPendingLeave = "close-panel" | "exit" | "upload-new" | null;

/**
 * MAIA page shell.
 *
 * Route: private/account/:accountId/dataset/:datasetId/maia
 *
 * Left panel  — the MAIA conversation (docs/AI/Entry Page.png).
 * Right panel — empty until "Upload new SOV file" is chosen, then upload,
 *               then (once a file parses) review, then (once submitted)
 *               geocode. Upload, review and geocode are independent sibling
 *               components — see maia-upload-step, maia-review-step and
 *               maia-geocode-step — coordinated here.
 *
 * Owns the "Leave this page?" confirmation, since every step's close control
 * and the step header's Exit can discard progress.
 */
@Component({
    selector: "maia-page",
    templateUrl: "./maia-page.component.html",
    styleUrls: ["./maia-page.component.scss"],
})
export class MaiaPageComponent implements OnInit {
    @ViewChild(MaiaUploadStepComponent) private readonly uploadStep?: MaiaUploadStepComponent;

    accountId: string | null = null;
    datasetId: string | null = null;

    get accountIdNumber(): number | null {
        return this.accountId ? Number(this.accountId) : null;
    }

    activePanel: MaiaRightPanel = null;

    /** The parsed response driving the review step, once upload finishes. */
    reviewData: SovUploadResponse | null = null;
    reviewFileSummary = "";
    reviewFileName = "";
    /** Gates Overview Report / Import on the ready screen until the user has clicked Download Locations. */
    locationsDownloaded = false;
    reviewJobTotalRows: number | null = null;

    /**
     * The job driving review/geocode/prepare/group — set as soon as upload
     * returns one, and the resume key once it's pushed into the URL (see
     * `pushJobIntoUrl`). Was `geocodeJobId`; renamed since it drives far more
     * than geocoding (docs/AI/MAIA-Wizard-Step-Cleanup-And-Resume-Plan.md §1.2).
     */
    jobId: number | null = null;
    geocodeTotalLocations = 0;
    /** false when re-entering geocode on an already-geocoded job (Back from enrich / resume) - don't re-run. */
    geocodeAutoStart = true;

    /** Best-effort last-known geocode sub-screen, for `onEnrichBack` (see comment there). */
    private lastGeocodeScreen: string = MaiaScreen.GEOCODING_COMPLETE;

    /** Set on resume when the job's `currentScreen` was already `import_success`. */
    resumedGroupView: MaiaGroupView | null = null;

    /** True once the portfolio has actually finished importing — drives maia-step-header's last step. */
    portfolioImported = false;
    /** True once the portfolio is imported - closing the success screen then has nothing to lose (no leave warning). */
    importCompleted = false;

    /** Whether the user selected paid overlays at the enrich step - the Enhancing screen waits for paid too. */
    enrichPaidSelected = false;

    /** What to do if the user confirms leaving. Null hides the dialog. */
    pendingLeave: MaiaPendingLeave = null;

    /**
     * True while a job named in the route is being fetched.
     *
     * Which panel to show comes from that job, so until it lands there is
     * nothing to render - and without this the wizard paints its header over
     * an empty body and then pops the panel in. Unlike the geocode step's own
     * version of this problem there is no screen to keep showing instead, so
     * this one needs a state of its own.
     */
    isResuming = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly location: Location,
        private readonly columnMappingService: MaiaColumnMappingService,
        private readonly uploadService: MaiaUploadService,
        private readonly notifService: NotifService
    ) {}

    ngOnInit(): void {
        this.accountId = this.route.snapshot.paramMap.get("accountId");
        this.datasetId = this.route.snapshot.paramMap.get("datasetId");

        // Testing convenience: ?panel=upload|enrich|report|group deep-links straight to a panel;
        // ?jobId= supplies the job for panels (enrich/enhance/report/group) that need it.
        const panel = this.route.snapshot.queryParamMap.get("panel");
        if (panel === "group" || panel === "enrich" || panel === "report" || panel === "upload") {
            this.activePanel = panel;
        }

        const jobIdParam = this.route.snapshot.paramMap.get("jobId");
        if (jobIdParam) {
            this.resumeJob(Number(jobIdParam));
            return;
        }

        // No job in the route, so there is nothing to resume: open the upload
        // screen. Landing on an empty shell used to be fine — the chat panel
        // beside it carried the "Upload new SOV file" entry — but with that
        // panel hidden this route would otherwise be a blank page with no way
        // forward. Guarded so the ?panel= deep-link above still wins.
        if (!this.activePanel) {
            this.activePanel = "upload";
        }
    }

    /**
     * Refresh/deep-link resume: the route carries a jobId, so fetch its
     * current state and land directly on the right screen instead of a blank
     * wizard. See docs/AI/MAIA-Wizard-Step-Cleanup-And-Resume-Plan.md §3.6.
     */
    private resumeJob(jobId: number): void {
        this.isResuming = true;
        this.uploadService.getJobState(jobId).subscribe({
            next: (job) => {
                this.isResuming = false;
                this.applyResumedJob(job);
            },
            error: () => this.recoverFromUnavailableJob(jobId),
        });
    }

    /**
     * The jobId in the URL couldn't be opened - not found (404), someone else's or another dataset's
     * (403), or not a number (400). Rather than silently dropping the user into a fresh wizard, take
     * them to the job they actually have open on this dataset, and say why either way.
     */
    private recoverFromUnavailableJob(jobId: number): void {
        console.warn(`MAIA: couldn't resume job ${jobId}.`);
        const requested = isNaN(jobId) ? "This job" : `Job #${jobId}`;

        if (!this.datasetId) {
            this.startFreshAfterUnavailableJob(requested);
            return;
        }

        this.uploadService.getActiveJobs(this.datasetId).subscribe({
            next: (jobs) => {
                // Normally 0 or 1; more only for leftover pre-SOV-013 jobs, so take the latest
                const active = jobs
                    .filter((job) => job.jobId !== jobId)
                    .sort((a, b) => this.updatedAtOf(b) - this.updatedAtOf(a))[0];
                if (!active) {
                    this.startFreshAfterUnavailableJob(requested);
                    return;
                }
                this.notifService.error(
                    `${requested} isn't available. Opened your active job #${active.jobId} instead.`
                );
                this.pushJobIntoUrl(active.jobId);
                // Not resumeJob(): if this one fails too, it must not loop back here
                this.uploadService.getJobState(active.jobId).subscribe({
                    next: (job) => {
                        this.isResuming = false;
                        this.applyResumedJob(job);
                    },
                    error: () => this.startFreshAfterUnavailableJob(`Job #${active.jobId}`),
                });
            },
            error: () => this.startFreshAfterUnavailableJob(requested),
        });
    }

    /** No job to fall back to: reset the URL and open Upload, so the page is never left empty. */
    private startFreshAfterUnavailableJob(requested: string): void {
        this.isResuming = false;
        this.notifService.error(`${requested} couldn't be opened. It doesn't exist or you don't have access to it.`);
        this.location.replaceState(this.baseMaiaUrl());
        this.activePanel = "upload";
    }

    private updatedAtOf(job: SovActiveJobSummary): number {
        const time = job.updatedAt != null ? new Date(job.updatedAt).getTime() : NaN;
        return isNaN(time) ? 0 : time;
    }

    private applyResumedJob(job: SovJobState): void {
        this.jobId = job.id;
        this.reviewFileName = job.fileName || "";
        this.reviewJobTotalRows = job.totalRows != null ? job.totalRows : null;
        this.geocodeTotalLocations = job.totalRows || 0;
        // Sheets are real; summary isn't available from this endpoint, so the
        // review screen shows real file/sheet info but no VAPT/geocode counts
        // until the user re-submits — a known, accepted gap (see plan doc).
        this.reviewData = {
            jobId: job.id,
            status: SovUploadStatus.PARSED,
            summary: null,
            sheets: job.sheets || [],
        };
        // maia-upload-step normally computes this (name/size/records/sheets)
        // off a live File — there is none on resume, so build it from the job
        // state directly, or the review card shows a blank summary row.
        this.reviewFileSummary = this.buildReviewFileSummary(job);

        this.activePanel = this.panelForScreen(job.currentScreen);
        // Only auto-run geocoding when resuming mid-geocode; a completed job shows results, no re-run.
        this.geocodeAutoStart = job.currentScreen === MaiaScreen.GEOCODING_IN_PROGRESS
            || job.currentScreen === MaiaScreen.GEOCODING_FAILED;
        // maia-group-step defaults to "grouping" — tell it explicitly when the
        // job already finished, or resuming into it would show the wrong view.
        this.resumedGroupView = job.currentScreen === MaiaScreen.IMPORT_SUCCESS ? "imported" : null;
        this.portfolioImported = job.currentScreen === MaiaScreen.IMPORT_SUCCESS;
        this.importCompleted = job.currentScreen === MaiaScreen.IMPORT_SUCCESS;
        // Resuming onto the Enhancing screen: restore whether paid was selected so it waits for paid too.
        if (job.currentScreen === MaiaScreen.PORTFOLIO_ENHANCING) {
            this.enrichPaidSelected = this.restorePaidSelected();
        }

        if (this.activePanel === "prepare") {
            this.loadPrepareMappings();
        }
    }

    /** Mirrors MaiaUploadStepComponent.fileSummary's "name · size · N rows · N columns" format. */
    private buildReviewFileSummary(job: SovJobState): string {
        if (!job.fileName) {
            return "";
        }
        const parts = [job.fileName];
        if (job.fileSize != null) {
            parts.push(formatFileSize(job.fileSize));
        }
        if (job.totalRows != null) {
            parts.push(`${job.totalRows.toLocaleString()} rows`);
        }
        const includedSheet = (job.sheets || []).find((sheet) => sheet.included && sheet.columnCount > 0);
        if (includedSheet) {
            parts.push(`${includedSheet.columnCount} columns`);
        }
        return parts.join(" · ");
    }

    /** Maps a persisted `currentScreen` key back to the wizard panel that shows it. */
    private panelForScreen(screen: string | null): MaiaRightPanel {
        switch (screen) {
            case MaiaScreen.UPLOAD_FILE:
                return "upload";
            case MaiaScreen.REVIEW_GEOCODING:
                return "review";
            case MaiaScreen.GEOCODING_IN_PROGRESS:
            case MaiaScreen.GEOCODING_COMPLETE:
            case MaiaScreen.GEOCODING_NEEDS_ATTENTION:
            case MaiaScreen.GEOCODING_FAILED:
                return "geocode";
            case MaiaScreen.PORTFOLIO_ENRICHMENT:
                return "enrich";
            case MaiaScreen.PORTFOLIO_ENHANCING:
                return "enhance";
            case MaiaScreen.PORTFOLIO_READY:
                return "ready";
            case MaiaScreen.ANALYZE_REPORT:
                return "report";
            case MaiaScreen.PREPARE_SOV:
                return "prepare";
            case MaiaScreen.GROUP_LOCATIONS:
            case MaiaScreen.IMPORT_SUCCESS:
                return "group";
            default:
                return "upload";
        }
    }

    private baseMaiaUrl(): string {
        return `private/account/${this.accountId}/dataset/${this.datasetId}/maia`;
    }

    /**
     * JIRA-SOV-013: the upload step found the user already has an active job on this dataset and they
     * chose "Resume job #N" — open that job exactly as a refresh on its URL would.
     */
    onResumeActiveJob(jobId: number): void {
        this.pushJobIntoUrl(jobId);
        this.resumeJob(jobId);
    }

    /** Puts `jobId` in the URL (no navigation/reload) so a refresh has something to resume. */
    private pushJobIntoUrl(jobId: number): void {
        this.location.replaceState(`${this.baseMaiaUrl()}/${jobId}`);
    }

    /** Fire-and-forget — see MaiaUploadService.updateWizardProgress. */
    private recordProgress(screen: string, completedSteps: MaiaWizardStep[] = []): void {
        if (this.jobId === null) {
            return;
        }
        this.uploadService.updateWizardProgress(this.jobId, screen, completedSteps).subscribe({
            error: (error) => console.error("Failed to record MAIA wizard progress", error),
        });
    }

    /**
     * Whether the MAIA chat panel shows at all.
     *
     * Off at the client's request: they don't want the assistant column for
     * now, so the wizard runs full width and the panel's one indispensable
     * control — "Upload new SOV file" — moved up into the step header beside
     * Exit. Flipping this back to true restores the column as it was; nothing
     * else needs changing, since the header keeps calling the same handler.
     */
    readonly chatPanelEnabled = false;

    /** The job MAIA chat should ground its answers in — whichever job is furthest along right now. */
    get chatJobId(): number | null {
        return this.jobId ?? this.reviewData?.jobId ?? null;
    }

    /** Drives maia-step-header. "geocode" is macro step "verify" — Enrich/Analyze/Save aren't built yet. */
    get currentFlowStep(): MaiaFlowStepId {
        if (this.activePanel === "review") {
            return "review";
        }
        if (this.activePanel === "geocode") {
            return "verify";
        }
        if (this.activePanel === "enrich" || this.activePanel === "enhance" || this.activePanel === "ready") {
            return "enrich";
        }
        if (this.activePanel === "report") {
            return "analyze";
        }
        if (this.activePanel === "prepare" || this.activePanel === "group") {
            return "save";
        }
        return "upload";
    }

    /** "Upload new SOV file" from the step header — confirm first if a job is mid-flow, else start fresh. */
    openUploadPanel(): void {
        if (this.hasUnsavedProgress() && !this.importCompleted) {
            this.pendingLeave = "upload-new";
            return;
        }
        this.startFreshUpload();
    }

    /**
     * Gives up the current job, then does `onClosed`.
     *
     * Every route back to a blank upload screen goes through here. The job
     * being walked away from still holds the dataset's one active-job slot, so
     * it has to be closed first — without that, picking a file on the screen
     * we are about to open is refused with "You already have an active SOV job
     * on this dataset".
     *
     * Read the jobId before `onClosed` runs: both continuations clear it.
     */
    private closeJobThen(onClosed: () => void): void {
        const jobId = this.jobId;
        if (jobId === null) {
            onClosed();
            return;
        }

        this.uploadService.closeJob(jobId).subscribe({
            next: () => onClosed(),
            // Deliberately stays put rather than opening the upload screen
            // anyway: the slot was not released, so the upload would fail too
            // — and this says why, at the moment it can still be acted on,
            // where that later failure would not. The common cause is a 409
            // while a stage is still running, which clears on its own.
            error: (error) => this.notifService.error(this.closeFailureMessage(error)),
        });
    }

    /**
     * `errors` before `message`: on this API's error body `message` is the
     * generic class of failure while `errors` carries the sentence naming the
     * job and what it is busy doing.
     */
    private closeFailureMessage(error: any): string {
        const body = error && error.error;
        const fallback =
            "We couldn't close the current job, so a new upload can't start yet. Please try again.";

        if (!body) {
            return fallback;
        }
        if (typeof body === "string" && body.trim()) {
            return body.trim();
        }
        if (Array.isArray(body.errors)) {
            const reasons = body.errors
                .filter((reason: unknown) => typeof reason === "string" && reason.trim())
                .map((reason: string) => reason.trim());
            if (reasons.length) {
                return reasons.join(" ");
            }
        }
        const message = body.message || body.error;
        return (typeof message === "string" && message.trim()) || fallback;
    }

    private startFreshUpload(): void {
        this.clearProgress();
        this.importCompleted = false;
        this.activePanel = "upload";
    }

    /** A file finished parsing — hand off from the upload step to the review step. */
    /**
     * The upload only updated records that already exist, so the backend
     * finished the job outright — those rows are already geocoded and
     * enriched. There is no wizard to run, so go straight back to the map.
     */
    onUploadAlreadyComplete(response: SovUploadResponse): void {
        this.jobId = response ? response.jobId : this.jobId;

        // Say what happened before leaving. Without this the user picks a file,
        // watches "Parsing…", and lands back on the map with no explanation —
        // which reads like the upload failed, when it actually succeeded.
        // totalRows arrives on the stream frame, not the upload POST, so it is
        // read off the step (same source onUploaded uses for the review screen).
        const rows = this.uploadStep ? this.uploadStep.jobTotalRows : null;
        this.notifService.success(
            rows !== null
                ? `${rows.toLocaleString()} existing location${rows === 1 ? "" : "s"} updated. ` +
                  `No geocoding was needed, so your file is already applied.`
                : "Your existing locations were updated. No geocoding was needed, so your file is already applied."
        );

        // navigateToMap, not exit(): exit() would raise the "leave this page?"
        // prompt over a job that has nothing left to lose.
        this.navigateToMap();
    }

    onUploaded(response: SovUploadResponse): void {
        this.reviewData = response;
        this.reviewFileSummary = this.uploadStep ? this.uploadStep.fileSummary : "";
        this.reviewFileName = this.uploadStep && this.uploadStep.selectedFile
            ? this.uploadStep.selectedFile.name
            : "";
        this.reviewJobTotalRows = this.uploadStep ? this.uploadStep.jobTotalRows : null;
        this.activePanel = "review";

        // Earliest point a jobId exists — put it in the URL so a refresh has
        // something to resume, even before the wizard progresses any further.
        this.jobId = response.jobId;
        this.pushJobIntoUrl(response.jobId);
        this.recordProgress(MaiaScreen.REVIEW_GEOCODING);
    }

    /** Review submitted — hand off to the geocode step. */
    onReviewSubmit(payload: MaiaReviewSubmit): void {
        this.jobId = payload.jobId;
        this.geocodeTotalLocations = payload.totalLocations;
        this.geocodeAutoStart = true;
        this.activePanel = "geocode";
        this.recordProgress(MaiaScreen.GEOCODING_IN_PROGRESS, [1]);
    }

    /** The geocode step's internal status settled on a screen worth remembering (see its `screenChanged`). */
    onGeocodeScreenChanged(screen: string): void {
        this.lastGeocodeScreen = screen;
        this.recordProgress(screen);
    }

    /**
     * Enrichment submitted. With paid datasets selected, the overlay stage is running -> show the
     * "Enhancing" progress screen; with none selected (skip), go straight to field-mapping. The
     * prepare step's column-match suggestions were already fetched on geocode-continue.
     */
    onEnrichSubmit(overlayIds: string[]): void {
        // Always show the "Enhancing" screen; it waits for free overlay (and paid, if selected) to
        // finish before advancing, so Download/Report never run on partially-enriched data.
        this.enrichPaidSelected = !!(overlayIds && overlayIds.length > 0);
        this.persistPaidSelected(this.enrichPaidSelected);
        this.activePanel = "enhance";
        this.recordProgress(MaiaScreen.PORTFOLIO_ENHANCING, [2]);
    }

    private paidSelectedKey(): string | null {
        return this.jobId == null ? null : `maia-enrich-paid-${this.jobId}`;
    }

    /** Remember whether paid overlays were chosen, so a refresh on the Enhancing screen still waits for paid. */
    private persistPaidSelected(selected: boolean): void {
        const key = this.paidSelectedKey();
        if (!key) {
            return;
        }
        try {
            localStorage.setItem(key, selected ? "1" : "0");
        } catch {
            // storage unavailable - resume falls back to free-only wait
        }
    }

    private restorePaidSelected(): boolean {
        const key = this.paidSelectedKey();
        if (!key) {
            return false;
        }
        try {
            return localStorage.getItem(key) === "1";
        } catch {
            return false;
        }
    }

    /** Enrichment progress finished -> "Portfolio ready". */
    onEnhanceComplete(): void {
        this.persistPaidSelected(false); // enrichment done; nothing left to wait for on a later resume
        this.locationsDownloaded = false; // require a fresh Download Locations before Overview/Import for this job
        this.activePanel = "ready";
        this.recordProgress(MaiaScreen.PORTFOLIO_READY);
    }

    /** Ready screen -> Overview Report (Analyze & Report). */
    onReadyOverviewReport(): void {
        this.activePanel = "report";
        this.recordProgress(MaiaScreen.ANALYZE_REPORT);
    }

    /** Ready screen -> Import to Maptycs: field-mapping (prepare) first, then grouping. */
    onReadyImport(): void {
        this.activePanel = "prepare";
        this.loadPrepareMappings();
        this.recordProgress(MaiaScreen.PREPARE_SOV, [3]);
    }

    /** Ready screen -> download the enriched portfolio file for this job. */
    onReadyDownload(): void {
        if (this.jobId == null) {
            return;
        }
        this.uploadService.downloadEnriched(this.jobId).subscribe({
            next: (blob) => {
                // The export is always .xlsx - use the file's base name but force the correct extension,
                // otherwise saving xlsx bytes under the original .csv name reads as a corrupted file.
                const base = (this.reviewFileName || `enriched-portfolio-${this.jobId}`).replace(/\.[^.]+$/, "");
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${base}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                this.locationsDownloaded = true;
            },
            // A failed download must not block the flow: warn and still unlock the next actions.
            error: () => {
                this.notifService.error("Couldn't download Locations. You can still continue.");
                this.locationsDownloaded = true;
            },
        });
    }

    /** Report "Back" -> the Portfolio ready screen it was launched from. */
    onReportBack(): void {
        this.activePanel = "ready";
        this.recordProgress(MaiaScreen.PORTFOLIO_READY);
    }

    /** Report "Continue" -> field-mapping (prepare) step. */
    onReportContinue(): void {
        this.activePanel = "prepare";
        this.loadPrepareMappings();
        this.recordProgress(MaiaScreen.PREPARE_SOV, [3]);
    }

    /** Fetch the column-match suggestions when the prepare step opens (not earlier - they may not be ready yet). */
    private loadPrepareMappings(): void {
        if (this.jobId == null) {
            return;
        }
        this.columnMappingService
            .getFieldMappings(this.jobId)
            .subscribe((mappings) => (this.prepareMappings = mappings));
    }

    /** Report "Back" -> enrichment selection (plain switch; no re-trigger of the overlay stage). */
    openEnrichPanel(): void {
        this.activePanel = "enrich";
    }

    /** Enrichment "Back" -> geocode results (already geocoded; don't re-run the service). */
    onEnrichBack(): void {
        this.geocodeAutoStart = false;
        this.activePanel = "geocode";
        // Best-effort: geocode-step doesn't expose which sub-screen it remounts
        // to, so this uses whatever it last reported rather than a fixed guess.
        this.recordProgress(this.lastGeocodeScreen);
    }

    /** Prepare accepted -> grouping / import step. */
    openGroupPanel(): void {
        this.activePanel = "group";
    }

    /** Grouping "Back" -> the field-mapping step (its data is already seeded). */
    openPreparePanel(): void {
        this.activePanel = "prepare";
        this.loadPrepareMappings();
        this.recordProgress(MaiaScreen.PREPARE_SOV);
    }

    /** Every group-step assignment approved — step 4's completion signal (see its `imported` output). */
    onGroupImported(): void {
        this.portfolioImported = true;
        this.importCompleted = true;
        this.recordProgress(MaiaScreen.IMPORT_SUCCESS, [4]);
    }

    onGroupClose(): void {
        // Nothing to lose once the portfolio is imported - skip the "unsaved progress" warning.
        this.requestCloseRightPanel(!this.importCompleted);
    }

    onGroupDownload(): void {
        this.onReadyDownload();
    }

    /*
     * The × was removed from every wizard screen at the client's request, so
     * nothing emits `closeRequested` today. The handlers below are kept — with
     * their bindings — so the panels' outputs stay wired to this page rather
     * than rotting, and putting the control back is a markup change only.
     */

    /** The wizard's × — confirm first when a file would be lost. */
    onUploadCloseRequested(hasUnsavedProgress: boolean): void {
        this.requestCloseRightPanel(hasUnsavedProgress);
    }

    /** The review card's × — reaching review always implies unsaved progress. */
    onReviewCloseRequested(hasUnsavedProgress: boolean): void {
        this.requestCloseRightPanel(hasUnsavedProgress);
    }

    /** The geocode card's × — a job in flight (or its result) always implies unsaved progress. */
    onGeocodeCloseRequested(hasUnsavedProgress: boolean): void {
        this.requestCloseRightPanel(hasUnsavedProgress);
    }

    /** Review's "Back" — same effect as "Replace file" today: no partial state survives a remount. */
    onReviewBack(): void {
        this.returnToUpload();
    }

    /**
     * Review's "Replace file" — the file on screen is being swapped for another
     * one, so the job it created is finished with. Closed exactly as "Upload
     * new SOV file" closes it, or its slot would block the replacement.
     *
     * No confirmation first: the user already said which file they want gone.
     */
    onReviewReplaceFile(): void {
        this.closeJobThen(() => this.returnToUpload());
    }

    /** Field mappings shown on the prepare step. */
    prepareMappings: SovFieldMapping[] = [];

    /**
     * "Continue to Enrichment" / "Continue without fixing" on the geocode step.
     * Enrich (macro step 4) isn't built yet, so this goes straight to the
     * field-mapping screen, fetching MAIA's column-match suggestions for the
     * job that was just geocoded.
     */
    onGeocodeContinue(): void {
        this.prepareMappings = [];
        this.activePanel = "enrich";
        this.recordProgress(MaiaScreen.PORTFOLIO_ENRICHMENT, [2]);

        if (this.jobId !== null && this.datasetId) {
            const jobId = this.jobId;
            // Trigger the free-overlay (core MAPTYCS) stage; fetch the
            // column-mapping suggestions only once that's settled, rather
            // than racing the two calls against each other.
            this.uploadService.triggerFreeOverlay(this.datasetId, jobId).subscribe({
                next: () => {
                    this.columnMappingService
                        .getFieldMappings(jobId)
                        .subscribe((mappings) => (this.prepareMappings = mappings));
                },
                error: (error) => console.error("Failed to trigger free overlay", error),
            });
        }
    }

    /** "Back" on the prepare step — returns to the enrichment step. */
    onPrepareBack(): void {
        this.activePanel = "enrich";
    }

    onPrepareCloseRequested(hasUnsavedProgress: boolean): void {
        if (hasUnsavedProgress) {
            this.pendingLeave = "close-panel";
            return;
        }
        this.activePanel = null;
    }

    /**
     * "Accept and Continue" — POSTs every column (visible and the hidden
     * root/coordinate ones alike) with whatever name the user ended up with.
     */
    onPrepareAccepted(mappings: SovFieldMapping[]): void {
        this.prepareMappings = mappings;

        if (this.jobId === null || !this.datasetId) {
            return;
        }
        const datasetId = this.datasetId;

        // Field-setup MUST finish first — it creates dataset_ai + the _ai drafts the group step
        // counts and filters on. Only then thematic-link them and open the group panel; opening
        // earlier shows a 0 count because the drafts don't exist yet.
        this.columnMappingService.submitFields(this.jobId, datasetId, mappings).subscribe({
            next: () => {
                // Link the freshly-created drafts in the background; grouping doesn't need to wait.
                this.uploadService.triggerThematicLink(datasetId).subscribe({
                    error: (error) => console.error("Failed to thematic-link AI drafts", error),
                });
                this.activePanel = "group";
                this.recordProgress(MaiaScreen.GROUP_LOCATIONS);
            },
            error: (error) => {
                // TODO: surface this once the prepare step has an error state.
                console.error("Failed to submit SOV field mappings", error);
            },
        });
    }

    /** "Exit & Autosave" returns to the map view this page was opened from. */
    exit(): void {
        if (this.hasUnsavedProgress()) {
            this.pendingLeave = "exit";
            return;
        }
        this.navigateToMap();
    }

    stayOnPage(): void {
        this.pendingLeave = null;
    }

    leaveWithoutSaving(): void {
        const action = this.pendingLeave;
        this.pendingLeave = null;

        if (action === "exit") {
            this.navigateToMap();
            return;
        }

        if (action === "upload-new") {
            this.closeJobThen(() => this.startFreshUpload());
            return;
        }

        this.clearProgress();
        this.activePanel = null;
    }

    private requestCloseRightPanel(hasUnsavedProgress: boolean): void {
        if (hasUnsavedProgress) {
            this.pendingLeave = "close-panel";
            return;
        }
        this.activePanel = null;
    }

    /** Back to a blank upload step — clears downstream state; the upload step remounts fresh. */
    private returnToUpload(): void {
        this.clearProgress();
        this.activePanel = "upload";
    }

    private clearProgress(): void {
        this.reviewData = null;
        this.reviewFileSummary = "";
        this.reviewFileName = "";
        this.reviewJobTotalRows = null;
        this.geocodeTotalLocations = 0;
        this.prepareMappings = [];
        this.jobId = null;
        this.lastGeocodeScreen = MaiaScreen.GEOCODING_COMPLETE;
        this.resumedGroupView = null;
        this.portfolioImported = false;
        // Drop the abandoned job's jobId from the URL — nothing left to resume to.
        this.location.replaceState(this.baseMaiaUrl());
    }

    private hasUnsavedProgress(): boolean {
        // The import already succeeded — nothing left to lose, so no "Leave this page?"
        // confirmation on Exit or "Go to Map View" from the imported-success screen.
        if (this.importCompleted) {
            return false;
        }
        if (this.activePanel && this.activePanel !== "upload") {
            return true;
        }
        return !!this.uploadStep && this.uploadStep.hasUnsavedProgress;
    }

    private navigateToMap(): void {
        this.router.navigate([
            `private/account/${this.accountId}/dataset/${this.datasetId}/datapoints/map`,
        ]);
    }
}
